import { useEffect, useState } from "react";
import { FileText, Printer, Eye, ExternalLink, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { BTN_PRIMARY, BTN_SOFT, BTN_SECONDARY, BTN_SM } from "./buttonStyles";
import { Badge } from "@/components/primitives";
import { formatApiError } from "@/lib/api";
import { docsAPI, openPdf, fmtINR, BILL_STATUS_TONE, billFileKind } from "@/lib/documents";
import { DownloadPdfButton } from "@/components/documents/DownloadPdfButton";
import { CategoryBillButtons } from "@/components/documents/CategorySplit";

/** Step 2 · provisional bill: summary + Generate / Show to Guest / Finalise / Print, editor opens in a new tab. */
export function BillStep({ stayId, onBillChange }) {
  const [summary, setSummary] = useState(null);
  const [bills, setBills] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [s, b] = await Promise.all([docsAPI.folioSummary(stayId), docsAPI.bills({ stay_id: stayId })]);
      setSummary(s); setBills(b); onBillChange?.(b);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [stayId]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = bills.find((b) => b.status !== "void");
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); if (msg) toast.success(msg); await load(); } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  if (!summary) return <div className="text-[13px] text-[#97A6A9] flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading bill…</div>;
  const rows = [...(summary.categories || []).map((c) => [c.label, c.total, `bill-cat-${c.key}`]), ["Discounts", -Math.abs(summary.discounts || 0), "bill-discounts"]];
  return (
    <div className="space-y-4" data-testid="bill-step">
      <div className="rounded-lg border border-[#D5E6E8] bg-white divide-y divide-[#EEF4F5]">
        {rows.map(([l, v, tid]) => (
          <div key={l} className="flex justify-between px-4 py-2 text-[13px]" data-testid={tid}><span className="text-[#3F5257]">{l}</span><span className="mono text-[#1A2425]">{fmtINR(v)}</span></div>
        ))}
        <div className="flex justify-between px-4 py-2 text-[13px]"><span className="text-[#3F5257]">Total charged</span><span className="mono font-semibold text-[#1A2425]">{fmtINR(summary.grand_total)}</span></div>
        <div className="flex justify-between px-4 py-2 text-[13px]"><span className="text-[#3F5257]">Paid so far</span><span className="mono text-[#14805E]">− {fmtINR(summary.amount_paid)}</span></div>
        <div className="flex justify-between px-4 py-2.5 text-[14px] bg-[#F4F8F9]"><span className="font-semibold text-[#1A2425]">Balance due</span>
          <span className={`mono font-bold ${summary.balance_due > 0 ? "text-[#B45309]" : "text-[#14805E]"}`} data-testid="bill-balance-due">{fmtINR(summary.balance_due)}</span></div>
      </div>

      <div className="rounded-lg border border-[#D5E6E8] bg-white p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[12px] font-semibold uppercase tracking-wider text-[#97A6A9]">Guest bill</div>
          {active && <Badge tone={BILL_STATUS_TONE[active.status] || "neutral"}>{active.status.replace(/_/g, " ")}</Badge>}
        </div>
        {!active ? (
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[13px] text-[#3F5257]">No bill generated yet for this stay.</div>
            <button type="button" onClick={() => run("gen", () => docsAPI.createProvisional(stayId), "Provisional bill generated")} disabled={!!busy} data-testid="bill-generate" className={`${BTN_SOFT} ${BTN_SM}`}>
              {busy === "gen" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}Generate Provisional Bill
            </button>
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            <div className="flex items-center gap-2 text-[13px]">
              <span className="mono font-semibold text-[#1A2425]" data-testid="bill-number">{active.bill_number}</span>
              <span className="text-[#97A6A9]">·</span><span className="mono text-[#3F5257]">{fmtINR(active.grand_total ?? summary.grand_total)}</span>
              {active.status === "finalised" && <span className="inline-flex items-center gap-1 text-[#14805E] font-semibold"><CheckCircle2 size={13} />Final</span>}
            </div>
            <div className="flex flex-wrap gap-2">
              {active.status !== "finalised" && (
                <a href={`/pms/stays/${stayId}/bill`} target="_blank" rel="noreferrer" className={`${BTN_SECONDARY} ${BTN_SM}`} data-testid="bill-edit">
                  <ExternalLink size={13} />Edit bill
                </a>
              )}
              {active.status === "draft" && (
                <button type="button" disabled={!!busy} onClick={() => run("show", () => docsAPI.showBill(active.id), "Bill marked as shown to guest")} data-testid="bill-show" className={`${BTN_SECONDARY} ${BTN_SM}`}>
                  <Eye size={13} />Show to Guest
                </button>
              )}
              {active.status !== "finalised" && (
                <button type="button" disabled={!!busy} onClick={() => run("fin", () => docsAPI.finaliseBill(active.id), "Bill finalised")} data-testid="bill-finalise" className={`${BTN_PRIMARY} ${BTN_SM}`}>
                  {busy === "fin" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={13} />}Finalise Bill
                </button>
              )}
              <button type="button" onClick={() => openPdf(docsAPI.billPdfUrl(active.id), `${billFileKind(active)}-${active.bill_number}.pdf`)} data-testid="bill-print" className={`${BTN_SECONDARY} ${BTN_SM}`}><Printer size={13} />Print</button>
              <DownloadPdfButton url={docsAPI.billPdfUrl(active.id)} filename={`${billFileKind(active)}-${active.bill_number}.pdf`} testId="bill-download" />
            </div>
            <CategoryBillButtons bill={active} size="md" />
          </div>
        )}
      </div>
    </div>
  );
}
